import axios, { AxiosError, AxiosInstance } from "axios"
import { Plugin } from "@nuxt/types"
import { isProcessClient } from "~/utils/process"
import backoff from "~/utils/backoff"

declare module "vue/types/vue" {
  interface Vue {
    $axios: AxiosInstance
  }
}

declare module "@nuxt/types" {
  interface NuxtAppOptions {
    $axios: AxiosInstance
  }
}

declare module "vuex/types/index" {
  interface Store<S> {
    $axios: AxiosInstance
  }
}

const authRetryStatuses = [401]
const authRetryLimit = 5
const backOffDelay = 2000

const axiosPlugin: Plugin = ({ store, app, req, isDev, $config }, inject) => {
  const axiosInstance = axios.create({
    baseURL: $config.SH_API_URL,
    timeout: 10000
  })

  // Set the host and protocol for our refresh request
  const host = isProcessClient() ? window.location.host : req.headers.host
  const protocol = isDev ? 'http://' : 'https://'
  const refreshTokenUrl = `${protocol}${host}/auth/refresh`

  axiosInstance.interceptors.request.use(config => {
    const token: string = store.state.oauth.accessToken || ''

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      }
    }

    return config
  })

  axiosInstance.interceptors.response.use(res => res, async (error: AxiosError) => {
    const { config, response } = error

    // Reject any incomplete request errors eg. Network Error
    if (!response) {
      return Promise.reject(error)
    }

    try {
      const { authRetry } = store.state
      if (authRetry > authRetryLimit) {
        // When we reach the retry limit end the user session
        app.$logout()
        app.$analytics.logout()
        return Promise.reject(error)
      } else if (authRetryStatuses.includes(response.status)) {
        store.commit('authRetry')

        const { data: { accessToken } } = await axios.get(
          refreshTokenUrl,
          { 
            headers: { Authorization: `Bearer ${store.state.oauth.accessToken}` }
          }
        )

        // Use an exponential back off after first retry (try first request instantly = 0)
        const { authRetry } = store.state
        const delayRequestTime = authRetry > 1 ? backOffDelay * authRetry : 0

        store.commit('oauth/setAccessToken', accessToken)
        store.commit('resetAuthRetry')

        // Retry the original request after the backoff
        return backoff(delayRequestTime).then(() => axiosInstance(config))
      }

      // Reject the response if it can't be retried
      return Promise.reject(error)
    } catch (error) {
      // Reject the response if it still fails after retry
      return Promise.reject(error)
    }
  })

  inject('axios', axiosInstance)
}

export default axiosPlugin
